import type { ChangeEvent } from 'react';

import get from 'lodash/get';
import isBoolean from 'lodash/isBoolean';
import isDate from 'lodash/isDate';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import mapValues from 'lodash/mapValues';
import toString from 'lodash/toString';
import type { ControllerRenderProps, FieldValues, UseFormHandleSubmit, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';

import { isEmpty, isNotEmptyString } from './checks';
import type { FormattingService } from './format';
import { DateFormat } from './format';
import type { Literal } from './guards';
import { isLiteral, isObjectDeepPath } from './guards';
import type {
  DeepObjectPaths, ReplaceObjectValues, FormPrefixes, ObjectShape, GetDiffArgs, Nullable,
  FormValues,
} from './object';
import { getDiff, replaceObjectValues } from './object';
import { withNamePrefix } from './string';

export type InteractivityState = boolean | string;

export const interactivityStateToBoolean = (
  state: Nullable<InteractivityState>,
): boolean => {
  if (isBoolean(state)) {
    return state;
  }

  if (isNil(state)) {
    return false;
  }

  return isNotEmptyString(state);
};

export const interactivityStateToReason = (
  state: Nullable<InteractivityState>,
): Nullable<string> => {
  if (isBoolean(state)) {
    return null;
  }

  if (isNil(state)) {
    return null;
  }

  return isNotEmptyString(state) ? state : null;
};

export const interactivityState = ({ when, reason }: Readonly<{
  when: boolean;
  reason: string;
}>): InteractivityState => (when ? reason : false);

type SchemaMappingFn = <T extends yup.AnySchema>(field: T, pathSoFar: string) => T;

export const makeRequired = (error?: string) => <T extends yup.AnySchema>(field: T): T => field.required(error) as T;
export const makeOptional = <T extends yup.AnySchema>(field: T): T => field.notRequired() as T;

type ModifySchemaArg<Schema extends yup.AnySchema> = Readonly<{
  schema: Schema;
  paths?: string[];
  mapField: SchemaMappingFn;
  compareFieldPaths?: (field: string, path: string) => boolean;
}>;

export const modifySchema = <Schema extends yup.AnySchema>({
  schema,
  paths,
  mapField,
  compareFieldPaths = (field, path) => field === path,
}: ModifySchemaArg<Schema>): Schema => {
  if (isEmpty(paths) && !isNil(paths)) {
    return schema;
  }

  const mapFields = <T extends yup.AnySchema>(field: T, pathSoFar?: string): T => {
    if (field instanceof yup.ObjectSchema) {
      return yup.object(mapValues(field.fields, (objectField, path) => {
        if (objectField instanceof yup.Schema) {
          return mapFields(objectField, withNamePrefix(pathSoFar, path));
        }
        return objectField as yup.AnySchema;
      })) as unknown as T;
    }

    if (isNil(paths)) {
      return mapField(field, '');
    }

    if (!isEmpty(pathSoFar) && paths.some((path) => compareFieldPaths(pathSoFar, path))) {
      return mapField(field, pathSoFar);
    }

    return field;
  };

  return mapFields(schema);
};

export const toEmptyForm = <Schema extends yup.AnySchema>(
  schema: Schema,
): Partial<yup.InferType<Schema>> => {
  if (schema instanceof yup.ObjectSchema) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return mapValues<yup.InferType<Schema>, any>(schema.fields as unknown, (field?: unknown) => {
      if (isNil(field) || typeof field !== 'object') {
        return null;
      }

      if (field instanceof yup.ObjectSchema) {
        return toEmptyForm(field);
      }

      if (field instanceof yup.StringSchema) {
        return '';
      }

      if (field instanceof yup.ArraySchema) {
        return [];
      }

      if (field instanceof yup.BooleanSchema) {
        return false;
      }

      if (field instanceof yup.NumberSchema) {
        return '';
      }

      return null;
    });
  }

  return {};
};

export const getTypedPrefixes = <Schema extends Literal>(
) => <T extends FormPrefixes<Schema>>(path: T): T => path;

const formatFieldValue = (config: MakeCreateDiffConfig) => (value?: unknown): string => {
  if (isNil(value) || isEmpty(value)) {
    return config.getEmptyText();
  }

  if (isString(value)) {
    return value;
  }

  if (isDate(value)) {
    return config.format.date(value, DateFormat.yearMonthDay);
  }

  if (isNumber(value)) {
    return value.toString();
  }

  if (isBoolean(value)) {
    return config.getBooleanText(value);
  }

  return '';
};

type AdaptedFormReturnValue<UserInput extends FieldValues> = Omit<UseFormReturn<FormValues<UserInput>>, 'handleSubmit'> & {
  handleSubmit: UseFormHandleSubmit<UserInput>
}

// while the user is filling out the form, fields could still be null, undefined, or strings in certain scenarios
// but RHF does parse them at the end / coerce them to the yup schema, which means the handleSubmit should receive the
// "correct" types
export const formAdapter = <UserInput extends FieldValues>(
  useFormReturnValue: UseFormReturn<FormValues<UserInput>>
): AdaptedFormReturnValue<UserInput> => useFormReturnValue as AdaptedFormReturnValue<UserInput>

type MakeCreateDiffConfig = Readonly<{
  format: FormattingService;
  getEmptyText: () => string;
  getChangeText: (arg: { field: string; previousValue: string; newValue: string }) => string;
  getBooleanText: (arg: boolean) => string;
}>;

type MakeCreateDiffArg<
  InitialDataShape extends Literal,
  InputShape extends ObjectShape<InitialDataShape>,
> = Pick<GetDiffArgs<InitialDataShape, InputShape>,'omitFields'> & Readonly<{
  initialData: GetDiffArgs<InitialDataShape, InputShape>['left'],
  userInput: GetDiffArgs<InitialDataShape, InputShape>['right']
  getFieldDisplayName: (
    fieldName: DeepObjectPaths<InitialDataShape> | DeepObjectPaths<InputShape>) => string;
  getCustomValues?: (
    fieldName: DeepObjectPaths<InitialDataShape> | DeepObjectPaths<InputShape>) => Nullable<{
    previousValue: unknown;
    currentValue: unknown;
  }>;
}>;

export const makeCreateFormDiff = (
  config: MakeCreateDiffConfig,
) => <InitialDataShape extends Literal, InputShape extends ObjectShape<InitialDataShape>>({
  omitFields,
  initialData,
  userInput,
  getFieldDisplayName,
  getCustomValues,
}: MakeCreateDiffArg<InitialDataShape, InputShape>,
): ReplaceObjectValues<ObjectShape<InitialDataShape>, unknown, string> => {
  const formatValue = formatFieldValue(config);
  const diff = getDiff<InitialDataShape, InputShape>({ omitFields, left: initialData, right: userInput });

  return (isLiteral(diff) ? replaceObjectValues(
    diff,
    isString,
    (_, path) => {
      if (!isObjectDeepPath(initialData, path) && !isObjectDeepPath(userInput, path)) {
        return null;
      }

      const customValues = getCustomValues?.(path);

      const previousValue: unknown = customValues
        ? customValues.previousValue
        : get(initialData, path);
      const currentValue: unknown = customValues
        ? customValues.currentValue
        : get(userInput, path);

      return config.getChangeText({
        field: getFieldDisplayName(path),
        previousValue: formatValue(previousValue),
        newValue: formatValue(currentValue),
      });
    },
  ) : {}) as ReplaceObjectValues<InputShape, unknown, string>;
};

const isMaskDigit = (maskChar: string) => maskChar === '9';
const isMaskLetter = (maskChar: string) => maskChar === 'a';
const isMaskAlphanumeric = (maskChar: string) => maskChar === '*';

const isDigit = (char: string) => /\d/.test(char);
const isLetter = (char: string) => /[a-zA-Z]/.test(char);
const isAlphanumeric = (char: string) => /[a-zA-Z0-9]/.test(char);

const matchesMaskToken = (char: string, maskChar: string) => {
  if (isMaskDigit(maskChar)) {
    return isDigit(char);
  }

  if (isMaskLetter(maskChar)) {
    return isLetter(char);
  }

  if (isMaskAlphanumeric(maskChar)) {
    return isAlphanumeric(char);
  }

  return char === maskChar;
};

type MaskAccumulator = Readonly<{
  result: string;
  inputIndex: number;
}>;

export const applyInputMask = (value: string, mask: string): string => {
  const inputChars = [...value];

  const { result } = [...mask].reduce<MaskAccumulator>((acc, maskChar) => {
    if (acc.inputIndex >= inputChars.length) {
      return acc;
    }

    if (isMaskDigit(maskChar) || isMaskLetter(maskChar) || isMaskAlphanumeric(maskChar)) {
      let { inputIndex } = acc;

      while (inputIndex < inputChars.length && !matchesMaskToken(inputChars[inputIndex], maskChar)) {
        inputIndex += 1;
      }

      if (inputIndex >= inputChars.length) {
        return { ...acc, inputIndex };
      }

      return {
        result: acc.result + inputChars[inputIndex],
        inputIndex: inputIndex + 1,
      };
    }

    let { inputIndex } = acc;

    if (inputIndex < inputChars.length && inputChars[inputIndex] === maskChar) {
      inputIndex += 1;
    }

    return {
      result: acc.result + maskChar,
      inputIndex,
    };
  }, { result: '', inputIndex: 0 });

  return result;
};

type MaskableField = Pick<ControllerRenderProps, 'name' | 'onBlur' | 'onChange' | 'ref' | 'value'>;

export const withInputMask = <RHFField extends MaskableField>(
  field: RHFField,
  mask: string,
): RHFField => ({
  ...field,
  value: applyInputMask(toString(field.value), mask),
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const maskedValue = applyInputMask(event.target.value, mask);

    field.onChange({
      ...event,
      target: {
        ...event.target,
        value: maskedValue,
      },
    });
  },
});

